import { useId, useRef, useState } from 'react';
import type { EmergencyStop } from '@traceio-org/platform-client';
import {
  DEFAULT_AGENT_HIGHEST_MODE,
  DEFAULT_INTELLIGENCE_LEVEL,
  MODULE_AGENT_CONFIG_KEYS,
  type AgentMode,
  type EffectiveModule,
  type IntelligenceLevel,
  type ModuleConfigurationResult,
} from '@traceio-org/platform-contracts';
import { PageSection, RecordStatus } from '@traceio-org/ui';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { RadioGroup, ReasonField, reasonError } from '../../components/fields';
import { DateText } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { ENTITLEMENT_SOURCE_LABELS, RELEASE_STATUS_LABELS, formatDateTime, productName } from '../../format';
import {
  AGENT_MODE_OPTIONS,
  INTELLIGENCE_LEVEL_KEY,
  INTELLIGENCE_LEVEL_OPTIONS,
  MODULE_STATE_PRESENTATION,
  REASON_LAYER_LABELS,
  canEnable,
  configurationSwitchText,
  agentModeLabel,
  agentSettingOf,
  agentSettingText,
  enabledDependents,
  hasAgentSetting,
  hasIntelligenceLevel,
  intelligenceLevelLabel,
  intelligenceLevelOf,
  isEnabled,
  missingDependencies,
} from '../../modules';
import { useAdminRead } from '../../useAdminRead';
import { useOrganizationScope, useSupportAccessHint } from './scope';

const CONNECTOR_LABELS: Readonly<Record<string, string>> = {
  mailbox: 'Mailbox connection',
  load_board: 'Load board connection',
};

/** Module states in which new work can run, so an emergency stop has something to stop. */
const RUNNING_STATES = new Set(['enabled', 'connected', 'degraded', 'spend_capped', 'billing_inactive']);

type ModuleDialog =
  | { kind: 'toggle'; moduleKey: string; enable: boolean }
  | { kind: 'level'; moduleKey: string }
  | { kind: 'agent'; moduleKey: string }
  | { kind: 'stop'; moduleKey: string | null }
  | { kind: 'release'; stop: EmergencyStop };

function nameOf(modules: readonly EffectiveModule[], key: string): string {
  return modules.find((m) => m.module.key === key)?.module.name ?? key;
}

function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The modules a configuration change also moved, as the notice after it says them. */
function affectedNote(result: ModuleConfigurationResult | null): string {
  if (!result || result.affected.length === 0) return '';
  const names = result.affected.map((m) => m.module.name);
  return ` ${listNames(names)} changed with it.`;
}

function EntitlementText({ module }: { module: EffectiveModule }) {
  const entitlement = module.entitlement;
  if (!entitlement) return <span className="admin-muted">None</span>;
  return (
    <>
      {ENTITLEMENT_SOURCE_LABELS[entitlement.source] ?? entitlement.source} since{' '}
      <DateText iso={entitlement.startsAt} />
      {entitlement.endsAt ? (
        <>
          , until <DateText iso={entitlement.endsAt} />
        </>
      ) : null}
    </>
  );
}

function ModuleCard({
  module,
  modules,
  canConfigure,
  stoppedOrganization,
  onAction,
}: {
  module: EffectiveModule;
  modules: readonly EffectiveModule[];
  canConfigure: boolean;
  stoppedOrganization: boolean;
  onAction: (dialog: ModuleDialog) => void;
}) {
  const headingId = useId();
  const definition = module.module;
  const presentation = MODULE_STATE_PRESENTATION[module.state];
  // The level is shown where it means something: a module the organization bought or has configured.
  const levelled =
    hasIntelligenceLevel(definition) && (module.state !== 'not_entitled' || module.configuration !== null);
  const level = intelligenceLevelOf(module);
  // The agent setting (catalog 2026-10-22.2) is shown under the same rule as the level.
  const agentShown =
    hasAgentSetting(definition) && (module.state !== 'not_entitled' || module.configuration !== null);
  const agent = agentSettingOf(module);
  const enabled = isEnabled(module);
  const releaseLabel =
    definition.releaseStatus === 'ga' ? null : RELEASE_STATUS_LABELS[definition.releaseStatus];
  return (
    <article className="admin-module" aria-labelledby={headingId} data-state={module.state}>
      <header className="admin-module__header">
        <div className="admin-module__title">
          <h3 id={headingId}>{definition.name}</h3>
          <code>{definition.key}</code>
          {releaseLabel ? <span className="admin-tag">{releaseLabel}</span> : null}
        </div>
        <RecordStatus tone={presentation.tone}>{presentation.label}</RecordStatus>
      </header>
      <p className="admin-module__description">{definition.description}</p>
      <p className="admin-module__explanation">{presentation.explanation}</p>
      {module.reasons.length > 0 ? (
        <ul
          className="admin-reasons"
          aria-label={`Why ${definition.name} is ${presentation.label.toLowerCase()}`}
        >
          {module.reasons.map((reason) => (
            <li key={`${reason.layer}:${reason.code}`}>
              <span className="admin-reasons__layer">
                {REASON_LAYER_LABELS[reason.layer] ?? reason.layer}:
              </span>{' '}
              {reason.message}
              {reason.actor?.displayName || reason.at ? (
                <span className="admin-muted">
                  {' '}
                  (
                  {[reason.actor?.displayName, reason.at ? formatDateTime(reason.at) : null]
                    .filter(Boolean)
                    .join(', ')}
                  )
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <dl className="admin-facts admin-facts--module" aria-label={`${definition.name} details`}>
        <div>
          <dt>Entitlement</dt>
          <dd>
            <EntitlementText module={module} />
          </dd>
        </div>
        {levelled ? (
          <div>
            <dt>Intelligence level</dt>
            <dd>
              {level
                ? intelligenceLevelLabel(level)
                : `${intelligenceLevelLabel(DEFAULT_INTELLIGENCE_LEVEL)} (the default)`}
            </dd>
          </div>
        ) : null}
        {agentShown ? (
          <div>
            <dt>Agent</dt>
            <dd>
              {agentSettingText(agent)}
              {/* An invalid configuration reads as the off products receive (fail closed), never its stored values. */}
              {agent.source === 'invalid'
                ? `: its stored configuration is invalid, so products read the agent as off at ${agentModeLabel(agent.highestMode)} until a valid one is saved`
                : agent.source === 'default'
                  ? ' (the default)'
                  : null}
            </dd>
          </div>
        ) : null}
        {definition.dependsOn.length > 0 ? (
          <div>
            <dt>Depends on</dt>
            <dd>{listNames(definition.dependsOn.map((key) => nameOf(modules, key)))}</dd>
          </div>
        ) : null}
        {definition.requiresConnectors.length > 0 ? (
          <div>
            <dt>Needs</dt>
            <dd>{listNames(definition.requiresConnectors.map((kind) => CONNECTOR_LABELS[kind] ?? kind))}</dd>
          </div>
        ) : null}
        <div>
          <dt>Configuration</dt>
          <dd>
            {module.configuration
              ? `${configurationSwitchText(module.configuration.enabled)}, version ${module.configuration.version}`
              : 'Never configured'}
          </dd>
        </div>
      </dl>
      {canConfigure ? (
        <div className="admin-module__actions">
          {enabled ? (
            <button
              type="button"
              className="admin-action"
              onClick={() => onAction({ kind: 'toggle', moduleKey: definition.key, enable: false })}
            >
              Turn off {definition.name}
            </button>
          ) : canEnable(module) ? (
            <button
              type="button"
              className="admin-action"
              onClick={() => onAction({ kind: 'toggle', moduleKey: definition.key, enable: true })}
            >
              Turn on {definition.name}
            </button>
          ) : null}
          {levelled && module.state !== 'not_entitled' ? (
            <button
              type="button"
              className="admin-action"
              onClick={() => onAction({ kind: 'level', moduleKey: definition.key })}
            >
              Change the intelligence level
            </button>
          ) : null}
          {agentShown && module.state !== 'not_entitled' ? (
            <button
              type="button"
              className="admin-action"
              onClick={() => onAction({ kind: 'agent', moduleKey: definition.key })}
            >
              Change the agent setting
            </button>
          ) : null}
          {RUNNING_STATES.has(module.state) && !stoppedOrganization ? (
            <button
              type="button"
              className="admin-action admin-action--danger"
              onClick={() => onAction({ kind: 'stop', moduleKey: definition.key })}
            >
              Emergency stop
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function StopRecord({
  stop,
  modules,
  canRelease,
  onRelease,
}: {
  stop: EmergencyStop;
  modules: readonly EffectiveModule[];
  canRelease: boolean;
  onRelease: () => void;
}) {
  const what = stop.scope === 'organization' ? 'Every module' : nameOf(modules, stop.moduleKey ?? '');
  return (
    <li className="admin-stop" data-scope={stop.scope}>
      <div>
        <p className="admin-stop__title">
          <strong>{what}</strong> stopped
        </p>
        <p>{stop.reason}</p>
        <p className="admin-muted">
          By {stop.stoppedBy}, <time dateTime={stop.stoppedAt}>{formatDateTime(stop.stoppedAt)}</time>
        </p>
      </div>
      {canRelease ? (
        <button type="button" className="admin-action" onClick={onRelease}>
          Release the stop of {what === 'Every module' ? 'every module' : what}
        </button>
      ) : null}
    </li>
  );
}

/**
 * Modules (/organizations/:id/modules): every module of the catalog for this organization with its effective
 * state and the reasons for it, its entitlement and configuration; staff who may configure modules turn them
 * on and off (with a reason and the version they saw), change the intelligence level, and start or release
 * emergency stops. Every change reads the modules again: one change can move others (dependents).
 */
export function ModulesTab() {
  const { organization, abilities } = useOrganizationScope();
  const hint = useSupportAccessHint();
  const { api } = useAdmin();
  const organizationId = organization.id;
  const modulesKey = `modules\u0000${organizationId}`;
  const modulesRead = useAdminRead(modulesKey, (signal) =>
    api.getOrganizationModules(organizationId, signal),
  );
  const stopsKey = `stops\u0000${organizationId}`;
  const stopsRead = useAdminRead(stopsKey, (signal) => api.listEmergencyStops(organizationId, signal));
  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<ModuleDialog>();
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  const [level, setLevel] = useState<IntelligenceLevel>(DEFAULT_INTELLIGENCE_LEVEL);
  const [agentOn, setAgentOn] = useState(false);
  const [agentMode, setAgentMode] = useState<AgentMode>(DEFAULT_AGENT_HIGHEST_MODE);

  const modules =
    modulesRead.dataKey === modulesKey && !modulesRead.failure ? modulesRead.data?.modules : null;
  const stops = stopsRead.dataKey === stopsKey && !stopsRead.failure ? stopsRead.data?.items : null;
  const activeStops = (stops ?? []).filter((stop) => stop.releasedAt === null);
  const releasedStops = (stops ?? []).filter((stop) => stop.releasedAt !== null);
  const organizationStop = activeStops.find((stop) => stop.scope === 'organization') ?? null;
  const name = organization.displayName;

  const open = (next: ModuleDialog) => {
    setReason('');
    setReasonProblem(null);
    if (next.kind === 'level' && modules) {
      const current = modules.find((m) => m.module.key === next.moduleKey);
      setLevel((current && intelligenceLevelOf(current)) ?? DEFAULT_INTELLIGENCE_LEVEL);
    }
    if (next.kind === 'agent' && modules) {
      const current = modules.find((m) => m.module.key === next.moduleKey);
      const setting = current ? agentSettingOf(current) : null;
      setAgentOn(setting?.enabled ?? false);
      setAgentMode(setting?.highestMode ?? DEFAULT_AGENT_HIGHEST_MODE);
    }
    dialog.show(next);
  };
  const reloadAll = () => {
    modulesRead.retry();
    stopsRead.retry();
  };
  const checkReason = (): boolean => {
    const problem = reasonError(reason);
    setReasonProblem(problem);
    return problem === null;
  };

  const target = dialog.target;
  const targetKey = target && target.kind !== 'release' ? target.moduleKey : null;
  const targetModule = targetKey ? (modules ?? []).find((m) => m.module.key === targetKey) : undefined;
  // The answer of the last configuration change, for the notice: the modules it moved with it.
  const lastResult = useRef<ModuleConfigurationResult | null>(null);

  // Each change sends the whole configuration as it was read, with its version: a change someone else made in
  // between is refused (409) instead of being overwritten. Only the turn on/off dialog sends `enabled`; the level and
  // agent dialogs leave it out, so a module nobody turned on or off stays available instead of becoming disabled.
  const sendConfiguration = async (
    module: EffectiveModule,
    enabled: boolean | undefined,
    config: Record<string, unknown>,
  ) => {
    return api.updateOrganizationModule(organizationId, module.module.key, {
      ...(enabled === undefined ? {} : { enabled }),
      config,
      expectedVersion: module.configuration?.version ?? 0,
      reason,
    });
  };

  let dialogView = null;
  if (target?.kind === 'toggle' && targetModule) {
    const moduleName = targetModule.module.name;
    const dependents = target.enable ? [] : enabledDependents(targetModule.module.key, modules ?? []);
    const missing = target.enable ? missingDependencies(targetModule, modules ?? []) : [];
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={target.enable ? `Turn on ${moduleName} for ${name}?` : `Turn off ${moduleName} for ${name}?`}
        submitLabel={target.enable ? `Turn on ${moduleName}` : `Turn off ${moduleName}`}
        tone={target.enable ? 'default' : 'danger'}
        submitHidden={missing.length > 0}
        cancelLabel={missing.length > 0 ? 'Close' : 'Cancel'}
        onSubmit={async () => {
          if (!checkReason()) return 'invalid';
          lastResult.current = null;
          lastResult.current = await sendConfiguration(
            targetModule,
            target.enable,
            targetModule.configuration?.config ?? {},
          );
          return undefined;
        }}
        onSaved={() => {
          setNotice(
            `${moduleName} is turned ${target.enable ? 'on' : 'off'}.${affectedNote(lastResult.current)}`,
          );
          reloadAll();
        }}
      >
        {missing.length > 0 ? (
          <p className="admin-dialog-warning">
            Turn on {listNames(missing.map((m) => m.module.name))} first: {moduleName} depends on it.
          </p>
        ) : (
          <>
            <p>
              {target.enable
                ? `${moduleName} can start new work for ${name} as soon as it is on.`
                : `No new ${moduleName.toLowerCase()} work starts for ${name}, and nothing more is sent for it. Its history is kept.`}
            </p>
            {dependents.length > 0 ? (
              <p className="admin-dialog-warning">
                {listNames(dependents.map((m) => m.module.name))}{' '}
                {dependents.length === 1 ? 'depends' : 'depend'} on {moduleName} and{' '}
                {dependents.length === 1 ? 'is' : 'are'} turned off too.
              </p>
            ) : null}
            <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
          </>
        )}
      </WriteDialog>
    );
  } else if (target?.kind === 'level' && targetModule) {
    const moduleName = targetModule.module.name;
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Change the intelligence level of ${moduleName}`}
        description="The level changes how many tokens each action uses, never the price of a token."
        submitLabel="Save the level"
        onSubmit={async () => {
          if (!checkReason()) return 'invalid';
          await sendConfiguration(targetModule, undefined, {
            ...(targetModule.configuration?.config ?? {}),
            [INTELLIGENCE_LEVEL_KEY]: level,
          });
          return undefined;
        }}
        onSaved={() => {
          setNotice(`The intelligence level of ${moduleName} is now ${intelligenceLevelLabel(level)}.`);
          reloadAll();
        }}
      >
        <RadioGroup
          legend="Intelligence level"
          choices={INTELLIGENCE_LEVEL_OPTIONS}
          value={level}
          onChange={(value) => setLevel(value as IntelligenceLevel)}
        />
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  } else if (target?.kind === 'agent' && targetModule) {
    const moduleName = targetModule.module.name;
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Change the agent setting of ${moduleName}`}
        description={`Whether the ${moduleName} agent works for ${name} and the furthest it may go. Only Trace changes this; the organization's administrators see it. The agent works only while ${moduleName} is on.`}
        submitLabel="Save the agent setting"
        onSubmit={async () => {
          if (!checkReason()) return 'invalid';
          await sendConfiguration(targetModule, undefined, {
            ...(targetModule.configuration?.config ?? {}),
            [MODULE_AGENT_CONFIG_KEYS.enabled]: agentOn,
            [MODULE_AGENT_CONFIG_KEYS.highestMode]: agentMode,
          });
          return undefined;
        }}
        onSaved={() => {
          setNotice(
            agentOn
              ? `The ${moduleName} agent is on, up to ${agentModeLabel(agentMode)}.`
              : `The ${moduleName} agent is off.`,
          );
          reloadAll();
        }}
      >
        <RadioGroup
          legend="Agent"
          choices={[
            { value: 'on', label: 'On', description: 'The agent works while the module is on.' },
            { value: 'off', label: 'Off', description: 'People do the work; the agent does nothing.' },
          ]}
          value={agentOn ? 'on' : 'off'}
          onChange={(value) => setAgentOn(value === 'on')}
        />
        <RadioGroup
          legend="Highest mode"
          choices={AGENT_MODE_OPTIONS}
          value={agentMode}
          onChange={(value) => setAgentMode(value as AgentMode)}
        />
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  } else if (target?.kind === 'stop') {
    const what = target.moduleKey ? (targetModule?.module.name ?? target.moduleKey) : null;
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={what ? `Emergency stop: ${what}` : `Emergency stop: every module of ${name}`}
        submitLabel={what ? `Stop ${what} now` : 'Stop every module now'}
        tone="danger"
        onSubmit={async () => {
          if (!checkReason()) return 'invalid';
          await api.startEmergencyStop(
            organizationId,
            target.moduleKey
              ? { scope: 'module', moduleKey: target.moduleKey, reason }
              : { scope: 'organization', reason },
          );
          return undefined;
        }}
        onSaved={() => {
          setNotice(what ? `${what} is stopped.` : `Every module of ${name} is stopped.`);
          reloadAll();
        }}
      >
        <p>
          {what ?? 'Every module'} stops at once for {name}: no new work starts and nothing more is sent,
          posted or booked. It stays stopped until someone releases the stop.
        </p>
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  } else if (target?.kind === 'release') {
    const what =
      target.stop.scope === 'organization'
        ? 'every module'
        : nameOf(modules ?? [], target.stop.moduleKey ?? '');
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Release the emergency stop of ${what}?`}
        submitLabel="Release the stop"
        onSubmit={async () => {
          if (!checkReason()) return 'invalid';
          await api.releaseEmergencyStop(organizationId, target.stop.id, reason);
          return undefined;
        }}
        onSaved={() => {
          setNotice(`The emergency stop of ${what} is released.`);
          reloadAll();
        }}
      >
        <p>
          {target.stop.scope === 'organization' ? 'Every module' : what} can start new work again, as its
          configuration allows.
        </p>
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  }

  // Products the organization subscribes to first, then the rest of the catalog, each in catalog order.
  const subscribed = new Set<string>(organization.products.map((product) => product.productKey));
  const groups = new Map<string, EffectiveModule[]>();
  for (const module of modules ?? []) {
    const group = groups.get(module.module.productKey) ?? [];
    group.push(module);
    groups.set(module.module.productKey, group);
  }
  const orderedGroups = [...groups.entries()].sort(
    ([a], [b]) => Number(subscribed.has(b)) - Number(subscribed.has(a)),
  );

  return (
    <>
      <SavedNotice message={notice} />
      <PageSection
        title="Emergency stops"
        description="A stop halts new work and every further email, posting and booking at once, until someone releases it."
        action={
          abilities.configureModules && !organizationStop && stops ? (
            <button
              type="button"
              className="admin-action admin-action--danger"
              onClick={() => open({ kind: 'stop', moduleKey: null })}
            >
              Stop every module
            </button>
          ) : undefined
        }
      >
        {stopsRead.failure ? (
          <AdminProblem
            failure={stopsRead.failure}
            subject="the emergency stops"
            onRetry={stopsRead.retry}
            supportAccess={hint}
          />
        ) : !stops ? (
          <LoadingBlock label="Loading the emergency stops" />
        ) : activeStops.length === 0 ? (
          <p className="admin-muted">No emergency stop is active.</p>
        ) : (
          <ul className="admin-stops" aria-label="Active emergency stops">
            {activeStops.map((stop) => (
              <StopRecord
                key={stop.id}
                stop={stop}
                modules={modules ?? []}
                canRelease={abilities.configureModules}
                onRelease={() => open({ kind: 'release', stop })}
              />
            ))}
          </ul>
        )}
        {releasedStops.length > 0 ? (
          <details className="admin-details">
            <summary>Released stops ({releasedStops.length})</summary>
            <ul className="admin-plain-list">
              {releasedStops.map((stop) => (
                <li key={stop.id}>
                  {stop.scope === 'organization'
                    ? 'Every module'
                    : nameOf(modules ?? [], stop.moduleKey ?? '')}
                  : stopped by {stop.stoppedBy} {formatDateTime(stop.stoppedAt)}, released by{' '}
                  {stop.releasedBy} {stop.releasedAt ? formatDateTime(stop.releasedAt) : ''}. {stop.reason}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </PageSection>
      {modulesRead.failure ? (
        <AdminProblem
          failure={modulesRead.failure}
          subject="the modules"
          onRetry={modulesRead.retry}
          supportAccess={hint}
        />
      ) : !modules ? (
        <LoadingBlock label="Loading the modules" />
      ) : modules.length === 0 ? (
        <p className="admin-muted">The module catalog is empty.</p>
      ) : (
        orderedGroups.map(([productKey, group]) => (
          <PageSection
            key={productKey}
            title={productName(productKey)}
            description={
              !subscribed.has(productKey)
                ? `${name} has no ${productName(productKey)} subscription. Its modules are listed for completeness.`
                : abilities.configureModules
                  ? 'Changes need a reason and apply at once; platform-api checks dependencies, entitlement and release.'
                  : undefined
            }
          >
            <div className="admin-modules" aria-busy={modulesRead.loading || undefined}>
              {group.map((module) => (
                <ModuleCard
                  key={module.module.key}
                  module={module}
                  modules={modules}
                  canConfigure={abilities.configureModules}
                  stoppedOrganization={organizationStop !== null}
                  onAction={open}
                />
              ))}
            </div>
          </PageSection>
        ))
      )}
      {dialogView}
    </>
  );
}
